import React, { useEffect } from "react";
import { formatCurrency } from "../config/market";
import { deliveryOfferLabel, deliveryOfferEventKey } from "../utils/deliveryOffers";
import { growthEventOnce } from "../utils/analytics";

/**
 * DeliveryFeeStatus — the ONE way every checkout screen renders the delivery
 * fee (and the fee-inclusive total) from useDeliveryFee().
 *
 * Contract (Playbook 12 — Order Placement Contract, §C3):
 *   • isReady   → render the formatted value.
 *   • loading   → "Calculating…" (transient, only while a quote is in flight).
 *   • isFailed  → visible error + a Retry action. NEVER a silent, permanent
 *                 "Calculating…" — that dead-ends checkout (July-2026 prod bug).
 *   • isBlocked → the quote can't even be requested (address has no coords):
 *                 tell the customer to re-select their delivery location.
 *
 * Keep this component dependency-free (plain spans, inline styles) — checkout
 * screens mix @material-ui v4 and @mui v5, so it must not bind to either.
 */
const styles = {
  error: { color: "#b71c1c", fontSize: 13, fontStyle: "italic" },
  blocked: { color: "#b26a00", fontSize: 13, fontStyle: "italic" },
  calculating: { color: "#607d8b", fontSize: 14, fontStyle: "italic" },
  struck: { color: "#607d8b", fontWeight: 400, marginRight: 4 },
  offerNote: { display: "block", fontSize: 12, fontWeight: 600, color: "#2c4d08" },
  retry: {
    marginLeft: 8,
    color: "#00668b",
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "underline",
  },
};

const DeliveryFeeStatus = ({
  isReady,
  isFailed,
  isBlocked,
  error,
  onRetry,
  value,
  freightQuotedSeparately = false,
  blockedMessage = "We couldn't read your delivery location — please go back and re-select your address.",
  failedMessage = "We couldn't calculate the delivery fee.",
  /* The quote's free-delivery offer (useDeliveryFee().offer). Pass it on the
     delivery row only, never on the total, so the row shows the full fee
     struck through and the reason it is lower. */
  offer = null,
}) => {
  const showOffer = Boolean(isReady && !freightQuotedSeparately && offer && offer.waivedAmount > 0);

  /* offer_applied: the checkout shows this shopper a waived fee. Once per
     offer and amount per page load (growthEventOnce). */
  useEffect(() => {
    if (!showOffer) return;
    growthEventOnce(`offer_applied:${offer.kind}:${offer.waivedAmount}`, "offer_applied", {
      offer: deliveryOfferEventKey(offer),
      waived_amount: offer.waivedAmount,
    });
  }, [showOffer, offer]);

  if (showOffer) {
    return (
      <span>
        <s style={styles.struck}>{formatCurrency(offer.originalFee)}</s> {value}
        <span style={styles.offerNote}>{deliveryOfferLabel(offer)}</span>
      </span>
    );
  }

  if (isReady) return <span>{freightQuotedSeparately ? "Quoted separately" : value}</span>;

  if (isFailed) {
    return (
      <span style={styles.error}>
        {typeof error === "string" && error.trim() ? error : failedMessage}
        {onRetry && (
          <button type="button" style={styles.retry} onClick={onRetry}>
            Retry
          </button>
        )}
      </span>
    );
  }

  if (isBlocked) {
    return <span style={styles.blocked}>{blockedMessage}</span>;
  }

  return <span style={styles.calculating}>Calculating…</span>;
};

/**
 * QuotedAmount: a checkout figure the SERVER priced (useDeliveryFee():
 * itemsSubtotal, total), in the delivery fee's own states: "Calculating..."
 * until the quote answers, a visible failure with Retry when it answered
 * without the figure, and the figure itself once known. Never a sum made on
 * the client. Pass `paymentProcessing` on the total: when the server added a
 * processing fee for the chosen payment method, the line says what it holds.
 */
export const QuotedAmount = ({
  amount,
  isReady,
  isFailed,
  isBlocked,
  error,
  onRetry,
  paymentProcessing = null,
  format = formatCurrency,
  failedMessage = "We couldn't work out this amount.",
}) => {
  const known = amount !== null && amount !== undefined && amount !== "" && Number.isFinite(Number(amount));
  const answeredWithout = Boolean(isReady) && !known;
  const cell = (
    <DeliveryFeeStatus
      isReady={Boolean(isReady) && known}
      isFailed={Boolean(isFailed) || answeredWithout}
      isBlocked={isBlocked}
      error={answeredWithout ? null : error}
      onRetry={onRetry}
      failedMessage={failedMessage}
      value={known ? format(Number(amount)) : ""}
    />
  );
  const processingFee = Number(paymentProcessing?.fee) || 0;
  if (!(isReady && known && processingFee > 0 && paymentProcessing?.show !== false)) return cell;
  return (
    <span>
      {cell}
      <span style={styles.offerNote}>
        Includes {paymentProcessing.label || "a payment processing fee"} of {format(processingFee)}
      </span>
    </span>
  );
};

export default DeliveryFeeStatus;
